/*
  ContextMenu component inspired by Motion's Base UI example.
  - Right-click to open at cursor position
  - Compound API similar to the referenced demo
  - Supports submenu and checkbox items

  Notes:
  - Animations use motion/react per workspace conventions
  - No external UI lib dependency
*/

'use client';

import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import './ContextMenu.css';

type Point = { x: number; y: number };

type RootContextValue = {
  open: boolean;
  setOpen: (v: boolean) => void;
  anchor: Point | null;
  setAnchor: (p: Point | null) => void;
  registerTrigger: (el: HTMLElement | null) => void;
};

const RootContext = React.createContext<RootContextValue | null>(null);

type SubmenuContextValue = {
  open: boolean;
  setOpen: (v: boolean) => void;
  anchorEl: HTMLElement | null;
  setAnchorEl: (el: HTMLElement | null) => void;
};

const SubmenuContext = React.createContext<SubmenuContextValue | null>(null);

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') {
        ref(value);
      } else {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    }
  };
}

function getElementRef<E>(element: React.ReactElement): React.Ref<E> | undefined {
  return (element as unknown as { ref?: React.Ref<E> }).ref;
}

export interface ContextMenuRootProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function useControllableBoolean(
  controlled: boolean | undefined,
  onChange: ((v: boolean) => void) | undefined,
) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const isControlled = controlled !== undefined;
  const value = isControlled ? controlled! : uncontrolled;
  const setValue = useCallback(
    (v: boolean) => {
      if (isControlled) onChange?.(v);
      else setUncontrolled(v);
    },
    [isControlled, onChange],
  );
  return [value, setValue] as const;
}

export function ContextMenuRoot({
  children,
  open: controlledOpen,
  onOpenChange,
}: ContextMenuRootProps) {
  const [open, setOpen] = useControllableBoolean(controlledOpen, onOpenChange);
  const [anchor, setAnchor] = useState<Point | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Global escape/blur/outside click to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const menu = document.querySelector('.contextmenu__content');
      if (menu && menu.contains(target)) return;
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onMouseDown, { capture: true });
    window.addEventListener('resize', () => setOpen(false));
    window.addEventListener('scroll', () => setOpen(false), true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onMouseDown, {
        capture: true,
      } as EventListenerOptions);
      window.removeEventListener('resize', () => setOpen(false));
      window.removeEventListener('scroll', () => setOpen(false), true);
    };
  }, [open, setOpen]);

  const registerTrigger = useCallback((el: HTMLElement | null) => {
    triggerRef.current = el;
  }, []);

  const value = useMemo<RootContextValue>(
    () => ({ open, setOpen, anchor, setAnchor, registerTrigger }),
    [open, setOpen, anchor, setAnchor, registerTrigger],
  );

  return <RootContext.Provider value={value}>{children}</RootContext.Provider>;
}

export type ContextMenuTriggerProps =
  | (React.HTMLAttributes<HTMLDivElement> & {
      asChild?: false;
      children: React.ReactNode;
      className?: string;
    })
  | ({
      asChild: true;
      children: React.ReactElement;
      className?: string;
    } & Omit<React.HTMLAttributes<HTMLElement>, 'children'>);

export function ContextMenuTrigger(props: ContextMenuTriggerProps) {
  const ctx = useContext(RootContext);
  if (!ctx) throw new Error('ContextMenu.Trigger must be used within ContextMenu.Root');
  const internalRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    ctx.registerTrigger(internalRef.current);
  }, [ctx]);

  if ('asChild' in props && props.asChild) {
    const {
      children,
      className = '',
      asChild: _omitAsChild, // prevent forwarding to child DOM element
      ...userRest
    } = props as Extract<ContextMenuTriggerProps, { asChild: true }>;
    void _omitAsChild;
    type ChildProps = React.HTMLAttributes<HTMLElement> & React.RefAttributes<HTMLElement>;
    const child = React.Children.only(children) as React.ReactElement<ChildProps>;

    const handleContextMenu: React.MouseEventHandler<HTMLElement> = (e) => {
      e.preventDefault();
      ctx.setAnchor({ x: e.clientX, y: e.clientY });
      ctx.setOpen(true);
      child.props.onContextMenu?.(e);
    };

    const mergedRef = mergeRefs<HTMLElement>(internalRef, getElementRef<HTMLElement>(child));

    const mergedClassName = [child.props.className, className].filter(Boolean).join(' ');

    return React.cloneElement<ChildProps>(child, {
      ...(userRest as Partial<ChildProps>),
      ref: mergedRef,
      className: mergedClassName || undefined,
      onContextMenu: handleContextMenu as unknown as ChildProps['onContextMenu'],
    } as Partial<ChildProps>);
  }

  const {
    children,
    className = '',
    asChild: _omitAsChild,
    ...rest
  } = props as Extract<ContextMenuTriggerProps, { asChild?: false }>;
  void _omitAsChild;

  return (
    <div
      ref={internalRef as React.RefObject<HTMLDivElement>}
      className={`contextmenu__trigger ${className}`}
      onContextMenu={(e) => {
        e.preventDefault();
        ctx.setAnchor({ x: e.clientX, y: e.clientY });
        ctx.setOpen(true);
        (rest as React.HTMLAttributes<HTMLDivElement>).onContextMenu?.(e);
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface ContextMenuPortalProps {
  children: React.ReactNode;
  keepMounted?: boolean;
}

export function ContextMenuPortal({ children, keepMounted = false }: ContextMenuPortalProps) {
  const ctx = useContext(RootContext);
  if (!ctx) throw new Error('ContextMenu.Portal must be used within ContextMenu.Root');
  const mounted = typeof window !== 'undefined';
  if (!mounted) return null;
  if (!keepMounted && !ctx.open) return null;
  return createPortal(children as React.ReactElement, document.body);
}

export interface ContextMenuPositionerProps {
  children: React.ReactNode;
}

export function ContextMenuPositioner({ children }: ContextMenuPositionerProps) {
  const ctx = useContext(RootContext);
  if (!ctx) throw new Error('ContextMenu.Positioner must be used within ContextMenu.Root');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!ctx.anchor) return;
    const update = () => {
      const { innerWidth: vw, innerHeight: vh } = window;
      const node = containerRef.current;
      // provisional placement at cursor
      let left = ctx.anchor!.x;
      let top = ctx.anchor!.y;

      if (node) {
        node.style.left = '0px';
        node.style.top = '0px';
        node.style.visibility = 'hidden';
        node.style.display = 'block';
        const rect = node.getBoundingClientRect();
        const maxLeft = Math.max(8, vw - rect.width - 8);
        const maxTop = Math.max(8, vh - rect.height - 8);
        left = Math.min(left, maxLeft);
        top = Math.min(top, maxTop);
        node.style.visibility = '';
        node.style.display = '';
      }

      setStyle({ left, top });
    };
    update();
  }, [ctx.anchor]);

  return (
    <div ref={containerRef} className="contextmenu__positioner" style={style ?? undefined}>
      {children}
    </div>
  );
}

export interface ContextMenuPopupProps {
  className?: string;
  // If provided, this node will be rendered as-is. Useful for wrapping with motion.div
  render?: React.ReactNode;
  children?: React.ReactNode;
}

export function ContextMenuPopup({ className = '', render, children }: ContextMenuPopupProps) {
  const ctx = useContext(RootContext);
  if (!ctx) throw new Error('ContextMenu.Popup must be used within ContextMenu.Root');

  const content = useMemo(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    const originX = ctx.anchor ? (ctx.anchor.x < vw / 2 ? 0 : 1) : 0;
    const originY = ctx.anchor ? (ctx.anchor.y < vh / 2 ? 0 : 1) : 0;

    if (render && React.isValidElement(render)) {
      const renderElement = render as React.ReactElement<{
        style?: React.CSSProperties;
        className?: string;
        role?: string;
      }>;
      const existingStyle = renderElement.props?.style ?? {};
      const style = {
        ...existingStyle,
        originX,
        originY,
        willChange: [existingStyle?.willChange, 'transform, opacity'].filter(Boolean).join(', '),
      } as React.CSSProperties;
      const mergedClassName = ['contextmenu__content', className, renderElement.props.className]
        .filter(Boolean)
        .join(' ');
      return React.cloneElement(renderElement, {
        style,
        className: mergedClassName || undefined,
        role: renderElement.props.role ?? 'menu',
        key: 'contextmenu-content',
      });
    }
    return (
      <motion.div
        key="contextmenu-content"
        role="menu"
        className={`contextmenu__content ${className}`}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.99 }}
        transition={{ duration: 0.14, opacity: { duration: 0.2 } }}
        style={{ originX, originY, willChange: 'transform, opacity' }}
      >
        {children}
      </motion.div>
    );
  }, [render, className, children, ctx.anchor]);

  return <AnimatePresence mode="wait">{ctx.open ? content : null}</AnimatePresence>;
}

export interface ContextMenuItemProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: React.ReactNode;
}

export function ContextMenuItem({
  children,
  className = '',
  disabled,
  ...rest
}: ContextMenuItemProps) {
  const ctx = useContext(RootContext);
  if (!ctx) throw new Error('ContextMenu.Item must be used within ContextMenu.Root');
  const { onClick, ...buttonRest } = rest;
  return (
    <button
      type="button"
      role="menuitem"
      className={`contextmenu__item ${className}`}
      data-disabled={disabled ? '' : undefined}
      disabled={disabled}
      onClick={(e) => {
        if (disabled) return;
        onClick?.(e);
        ctx.setOpen(false);
      }}
      {...buttonRest}
    >
      {children}
    </button>
  );
}

export interface ContextMenuSeparatorProps {
  className?: string;
}

export function ContextMenuSeparator({ className = '' }: ContextMenuSeparatorProps) {
  return <div role="separator" className={`contextmenu__separator ${className}`} />;
}

export interface ContextMenuCheckboxItemProps {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  children: React.ReactNode;
  className?: string;
}

export function ContextMenuCheckboxItem({
  checked,
  onCheckedChange,
  children,
  className = '',
}: ContextMenuCheckboxItemProps) {
  const ctx = useContext(RootContext);
  if (!ctx) throw new Error('ContextMenu.CheckboxItem must be used within ContextMenu.Root');
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      className={`contextmenu__item ${className}`}
      onClick={() => onCheckedChange(!checked)}
    >
      {children}
    </button>
  );
}

export interface ContextMenuCheckboxItemIndicatorProps {
  className?: string;
  children?: React.ReactNode;
}

export function ContextMenuCheckboxItemIndicator({
  className = '',
  children,
}: ContextMenuCheckboxItemIndicatorProps) {
  return <span className={`contextmenu__indicator ${className}`}>{children}</span>;
}

export interface ContextMenuSubmenuRootProps {
  children: React.ReactNode;
}

function ContextMenuSubmenuRootImpl({ children }: ContextMenuSubmenuRootProps) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const value = useMemo<SubmenuContextValue>(
    () => ({ open, setOpen, anchorEl, setAnchorEl }),
    [open, anchorEl],
  );
  return <SubmenuContext.Provider value={value}>{children}</SubmenuContext.Provider>;
}

export interface ContextMenuSubmenuTriggerProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: React.ReactNode;
  className?: string;
}

function ContextMenuSubmenuTriggerImpl({
  children,
  className = '',
  ...rest
}: ContextMenuSubmenuTriggerProps) {
  const ctx = useContext(SubmenuContext);
  if (!ctx)
    throw new Error('ContextMenu.SubmenuTrigger must be used within ContextMenu.SubmenuRoot');
  const ref = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    ctx.setAnchorEl(ref.current);
  }, [ctx]);
  return (
    <button
      type="button"
      role="menuitem"
      ref={ref}
      className={`contextmenu__item ${className}`}
      onMouseEnter={() => ctx.setOpen(true)}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface ContextMenuSubmenuPopupProps {
  className?: string;
  render?: React.ReactNode;
  children?: React.ReactNode;
}

function ContextMenuSubmenuPopupImpl({
  className = '',
  render,
  children,
}: ContextMenuSubmenuPopupProps) {
  const ctx = useContext(SubmenuContext);
  if (!ctx) throw new Error('ContextMenu.Popup must be used within ContextMenu.SubmenuRoot');
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!ctx.anchorEl) return;
    const rect = ctx.anchorEl.getBoundingClientRect();
    const left = rect.right + 6;
    const top = rect.top;
    setPos({ left, top });
  }, [ctx.anchorEl, ctx.open]);

  return (
    <AnimatePresence mode="wait">
      {ctx.open ? (
        <div
          ref={ref}
          className="contextmenu__submenu-positioner"
          style={pos ?? undefined}
          onMouseEnter={() => ctx.setOpen(true)}
          onMouseLeave={() => ctx.setOpen(false)}
        >
          {render && React.isValidElement(render) ? (
            (() => {
              const renderElement = render as React.ReactElement<{
                className?: string;
                role?: string;
                style?: React.CSSProperties;
              }>;
              const mergedClassName = [
                'contextmenu__content',
                className,
                renderElement.props.className,
              ]
                .filter(Boolean)
                .join(' ');
              return React.cloneElement(renderElement, {
                className: mergedClassName || undefined,
                role: renderElement.props.role ?? 'menu',
                style: {
                  ...renderElement.props.style,
                  willChange: [renderElement.props.style?.willChange, 'transform, opacity']
                    .filter(Boolean)
                    .join(', '),
                } as React.CSSProperties,
              });
            })()
          ) : (
            <motion.div
              role="menu"
              className={`contextmenu__content ${className}`}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -4 }}
              transition={{ duration: 0.14, opacity: { duration: 0.2 } }}
              style={{ willChange: 'transform, opacity' }}
            >
              {children}
            </motion.div>
          )}
        </div>
      ) : null}
    </AnimatePresence>
  );
}

export type ContextMenuCompound = {
  Root: typeof ContextMenuRoot;
  Trigger: typeof ContextMenuTrigger;
  Portal: typeof ContextMenuPortal;
  Positioner: typeof ContextMenuPositioner;
  Popup: typeof ContextMenuPopup;
  Item: typeof ContextMenuItem;
  Separator: typeof ContextMenuSeparator;
  CheckboxItem: typeof ContextMenuCheckboxItem;
  CheckboxItemIndicator: typeof ContextMenuCheckboxItemIndicator;
  SubmenuRoot: typeof ContextMenuSubmenuRootImpl;
  SubmenuTrigger: typeof ContextMenuSubmenuTriggerImpl;
  SubmenuPopup: typeof ContextMenuSubmenuPopupImpl;
};

export const ContextMenu: ContextMenuCompound = {
  Root: ContextMenuRoot,
  Trigger: ContextMenuTrigger,
  Portal: ContextMenuPortal,
  Positioner: ContextMenuPositioner,
  Popup: ContextMenuPopup,
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
  CheckboxItem: ContextMenuCheckboxItem,
  CheckboxItemIndicator: ContextMenuCheckboxItemIndicator,
  SubmenuRoot: ContextMenuSubmenuRootImpl,
  SubmenuTrigger: ContextMenuSubmenuTriggerImpl,
  SubmenuPopup: ContextMenuSubmenuPopupImpl,
};

export default ContextMenu;

export function useContextMenu() {
  const ctx = useContext(RootContext);
  if (!ctx) throw new Error('useContextMenu must be used within ContextMenu.Root');
  return {
    open: ctx.open,
    setOpen: ctx.setOpen,
    setAnchor: ctx.setAnchor,
  };
}
